import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ExperimentCard from './components/ExperimentCard';
import CodeViewer from './components/CodeViewer';
import ExperimentFilters from './components/ExperimentFilters';
import ExperimentStats from './components/ExperimentStats';
import ParticleBackground from './components/ParticleBackground';

const ExperimentalPlayground = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [isCodeViewerOpen, setIsCodeViewerOpen] = useState(false);
  const [filteredExperiments, setFilteredExperiments] = useState([]);

  // Mock experiments data
  const experiments = [
    {
      id: 1,
      title: "Neural Network Visualizer",
      description: "Real-time visualization of neural network training with interactive parameter adjustment. Watch as the network learns and adapts through WebGL-powered animations.",
      preview: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop",
      status: "active",
      category: "ai",
      technologies: ["Three.js", "TensorFlow.js", "WebGL", "React"],
      lastUpdated: "2 days ago",
      views: 1247,
      votes: 89,
      difficulty: 4,
      parameters: [
        { name: "Learning Rate", min: 0.001, max: 0.1, value: 0.01 },
        { name: "Batch Size", min: 16, max: 128, value: 32 }
      ],
      features: [
        "Real-time training visualization",
        "Interactive parameter tuning",
        "Multiple network architectures",
        "Performance metrics display"
      ],
      performanceNotes: "Optimized for 60fps on modern GPUs. Uses WebGL compute shaders for parallel processing.",
      code: {
        implementation: `// Neural Network Visualizer Implementation
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

const NeuralNetworkVisualizer = ({ learningRate, batchSize }) => {
  const meshRef = useRef();
  const [neurons, setNeurons] = useState([]);
  
  useEffect(() => {
    // Initialize neural network structure
    const networkLayers = [784, 128, 64, 10];
    const neuronPositions = [];
    
    networkLayers.forEach((layerSize, layerIndex) => {
      for (let i = 0; i < layerSize; i++) {
        neuronPositions.push({
          x: layerIndex * 4,
          y: (i - layerSize / 2) * 0.5,
          z: 0,
          activation: Math.random()
        });
      }
    });
    
    setNeurons(neuronPositions);
  }, []);

  useFrame((state) => {
    if (meshRef.current) {
      // Animate neural network activity
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.1;
    }
  });

  return (
    <group ref={meshRef}>
      {neurons.map((neuron, index) => (
        <mesh key={index} position={[neuron.x, neuron.y, neuron.z]}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshStandardMaterial 
            color={new THREE.Color().setHSL(neuron.activation, 1, 0.5)}
            emissive={new THREE.Color().setHSL(neuron.activation, 1, 0.2)}
          />
        </mesh>
      ))}
    </group>
  );
};

export default NeuralNetworkVisualizer;`,
        vertexShader: `// Vertex Shader for Neural Network Nodes
attribute float activation;
attribute vec3 position;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
varying float vActivation;

void main() {
  vActivation = activation;
  vec3 pos = position;
  
  // Pulse effect based on activation
  pos *= 1.0 + activation * 0.3;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  gl_PointSize = 10.0 + activation * 20.0;
}`,
        fragmentShader: `// Fragment Shader for Neural Network Nodes
varying float vActivation;

void main() {
  vec2 center = gl_PointCoord - 0.5;
  float dist = length(center);
  
  if (dist > 0.5) discard;
  
  vec3 color = mix(
    vec3(0.0, 0.2, 1.0),
    vec3(1.0, 0.4, 0.0),
    vActivation
  );
  
  float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
  gl_FragColor = vec4(color, alpha);
}`
      },
      documentation: {
        overview: "This experiment demonstrates real-time neural network training visualization using WebGL shaders and Three.js. The system renders thousands of neurons with dynamic activation states.",
        approach: "Uses instanced rendering for performance, with custom shaders handling the visual representation of neural activity. TensorFlow.js integration provides actual machine learning capabilities.",
        challenges: [
          "Rendering thousands of neurons at 60fps",
          "Synchronizing ML training with visual updates",
          "Managing GPU memory for large networks"
        ],
        improvements: [
          "Add support for convolutional layers",
          "Implement gradient flow visualization",
          "Add export functionality for trained models"
        ]
      }
    },
    {
      id: 2,
      title: "Procedural Terrain Generator",
      description: "Generate infinite landscapes using Perlin noise and WebGL compute shaders. Features real-time editing, multiple biomes, and physics simulation.",
      preview: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      status: "beta",
      category: "webgl",
      technologies: ["WebGL", "Compute Shaders", "Perlin Noise", "Physics"],
      lastUpdated: "1 week ago",
      views: 892,
      votes: 67,
      difficulty: 5,
      parameters: [
        { name: "Octaves", min: 1, max: 8, value: 4 },
        { name: "Frequency", min: 0.01, max: 0.1, value: 0.05 }
      ],
      features: [
        "Infinite terrain generation",
        "Multiple biome support",
        "Real-time editing tools",
        "Physics-based water simulation"
      ],
      performanceNotes: "Uses compute shaders for terrain generation. Requires WebGL 2.0 support.",
      code: {
        implementation: `// Procedural Terrain Generator
class TerrainGenerator {
  constructor(size = 512) {
    this.size = size;
    this.heightMap = new Float32Array(size * size);
    this.initializeNoise();
  }
  
  initializeNoise() {
    // Initialize Perlin noise generator
    this.noise = new PerlinNoise();
  }
  
  generateTerrain(octaves = 4, frequency = 0.05) {
    for (let x = 0; x < this.size; x++) {
      for (let z = 0; z < this.size; z++) {
        let height = 0;
        let amplitude = 1;
        let freq = frequency;
        
        for (let i = 0; i < octaves; i++) {
          height += this.noise.noise(x * freq, z * freq) * amplitude;
          amplitude *= 0.5;
          freq *= 2;
        }
        
        this.heightMap[x + z * this.size] = height;
      }
    }
    
    return this.heightMap;
  }
  
  getBiome(height, temperature, humidity) {
    if (height < 0.3) return 'water';
    if (height < 0.5 && temperature > 0.7) return 'desert';
    if (height > 0.8) return 'mountain';
    return 'grassland';
  }
}`,
        vertexShader: `// Terrain Vertex Shader
attribute vec3 position;
attribute vec2 uv;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform sampler2D heightMap;
uniform float heightScale;
varying vec2 vUv;
varying float vHeight;

void main() {
  vUv = uv;
  
  // Sample height from texture
  float height = texture2D(heightMap, uv).r * heightScale;
  vHeight = height;
  
  vec3 pos = position;
  pos.y = height;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`,
        fragmentShader: `// Terrain Fragment Shader
varying vec2 vUv;
varying float vHeight;

vec3 getTerrainColor(float height) {
  if (height < 0.3) return vec3(0.2, 0.4, 0.8); // Water
  if (height < 0.5) return vec3(0.8, 0.7, 0.3); // Sand
  if (height < 0.7) return vec3(0.3, 0.7, 0.2); // Grass
  return vec3(0.6, 0.6, 0.6); // Rock
}

void main() {
  vec3 color = getTerrainColor(vHeight);
  gl_FragColor = vec4(color, 1.0);
}`
      },
      documentation: {
        overview: "Advanced procedural terrain generation system using multiple noise functions and biome classification algorithms.",
        approach: "Combines multiple octaves of Perlin noise with temperature and humidity maps to create realistic terrain variations.",
        challenges: [
          "Seamless chunk loading for infinite worlds",
          "Optimizing noise calculations for real-time generation",
          "Balancing visual quality with performance"
        ],
        improvements: [
          "Add erosion simulation",
          "Implement vegetation placement",
          "Add weather system integration"
        ]
      }
    },
    {
      id: 3,
      title: "Voice-Controlled 3D Interface",
      description: "Navigate and manipulate 3D environments using voice commands. Features speech recognition, natural language processing, and gesture synthesis.",
      preview: "https://images.unsplash.com/photo-1589254065878-42c9da997008?w=400&h=300&fit=crop",
      status: "concept",
      category: "interaction",
      technologies: ["Web Speech API", "NLP", "Three.js", "Machine Learning"],
      lastUpdated: "3 days ago",
      views: 634,
      votes: 45,
      difficulty: 3,
      parameters: [
        { name: "Sensitivity", min: 0.1, max: 1.0, value: 0.7 },
        { name: "Response Time", min: 100, max: 1000, value: 300 }
      ],
      features: [
        "Natural language commands",
        "Real-time speech recognition",
        "3D object manipulation",
        "Context-aware responses"
      ],
      performanceNotes: "Requires microphone access. Works best in quiet environments.",
      code: {
        implementation: `// Voice-Controlled 3D Interface
class VoiceController {
  constructor() {
    this.recognition = new webkitSpeechRecognition();
    this.isListening = false;
    this.commands = new Map();
    this.setupRecognition();
    this.setupCommands();
  }
  
  setupRecognition() {
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';
    
    this.recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript;
      this.processCommand(transcript.toLowerCase().trim());
    };
  }
  
  setupCommands() {
    this.commands.set('rotate left', () => this.rotateScene(-90));
    this.commands.set('rotate right', () => this.rotateScene(90));
    this.commands.set('zoom in', () => this.zoomCamera(0.8));
    this.commands.set('zoom out', () => this.zoomCamera(1.2));
    this.commands.set('reset view', () => this.resetCamera());
  }
  
  processCommand(transcript) {
    for (let [command, action] of this.commands) {
      if (transcript.includes(command)) {
        action();
        this.provideFeedback(\`Executed: \${command}\`);
        break;
      }
    }
  }
  
  startListening() {
    if (!this.isListening) {
      this.recognition.start();
      this.isListening = true;
    }
  }
  
  stopListening() {
    if (this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    }
  }
}`,
        vertexShader: `// Voice Visualization Vertex Shader
attribute vec3 position;
attribute float frequency;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform float time;
uniform float amplitude;
varying float vFrequency;

void main() {
  vFrequency = frequency;
  
  vec3 pos = position;
  pos.y += sin(time * frequency * 10.0) * amplitude * 0.5;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`,
        fragmentShader: `// Voice Visualization Fragment Shader
varying float vFrequency;

void main() {
  vec3 color = mix(
    vec3(0.0, 1.0, 0.5),
    vec3(1.0, 0.0, 0.5),
    vFrequency
  );
  
  gl_FragColor = vec4(color, 0.8);
}`
      },
      documentation: {
        overview: "Experimental interface that allows users to control 3D environments through natural speech commands.",
        approach: "Uses Web Speech API for recognition, combined with NLP for command interpretation and Three.js for 3D manipulation.",
        challenges: [
          "Handling ambient noise and accents",
          "Mapping natural language to 3D actions",
          "Providing clear visual feedback"
        ],
        improvements: [
          "Add multi-language support",
          "Implement gesture recognition",
          "Add voice training capabilities"
        ]
      }
    },
    {
      id: 4,
      title: "Quantum Particle Simulator",
      description: "Visualize quantum mechanics principles through interactive particle simulations. Features wave-particle duality, quantum tunneling, and superposition states.",
      preview: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=300&fit=crop",
      status: "active",
      category: "emerging",
      technologies: ["WebGL", "Quantum.js", "Complex Math", "Physics"],
      lastUpdated: "5 days ago",
      views: 1156,
      votes: 78,
      difficulty: 5,
      parameters: [
        { name: "Particle Count", min: 100, max: 10000, value: 1000 },
        { name: "Wave Function", min: 0.1, max: 2.0, value: 1.0 }
      ],
      features: [
        "Wave-particle duality visualization",
        "Quantum tunneling simulation",
        "Superposition state rendering",
        "Interactive measurement tools"
      ],
      performanceNotes: "Computationally intensive. Requires high-end GPU for optimal performance.",
      code: {
        implementation: `// Quantum Particle Simulator
class QuantumSimulator {
  constructor(particleCount = 1000) {
    this.particleCount = particleCount;
    this.particles = [];
    this.waveFunction = new ComplexArray(particleCount);
    this.initializeParticles();
  }
  
  initializeParticles() {
    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 10
        ),
        momentum: new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2
        ),
        phase: Math.random() * Math.PI * 2,
        probability: 1.0 / this.particleCount
      });
    }
  }
  
  updateWaveFunction(deltaTime) {
    for (let i = 0; i < this.particleCount; i++) {
      const particle = this.particles[i];
      
      // Schrödinger equation simulation
      const kineticEnergy = particle.momentum.lengthSq() / (2 * PARTICLE_MASS);
      const potentialEnergy = this.getPotentialEnergy(particle.position);
      
      particle.phase += (kineticEnergy + potentialEnergy) * deltaTime / HBAR;
      
      // Update probability amplitude
      this.waveFunction.real[i] = Math.cos(particle.phase) * Math.sqrt(particle.probability);
      this.waveFunction.imag[i] = Math.sin(particle.phase) * Math.sqrt(particle.probability);
    }
  }
  
  measureParticle(index) {
    const probability = this.waveFunction.real[index] ** 2 + this.waveFunction.imag[index] ** 2;
    return Math.random() < probability;
  }
}`,
        vertexShader: `// Quantum Particle Vertex Shader
attribute vec3 position;
attribute float phase;
attribute float probability;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform float time;
varying float vPhase;
varying float vProbability;

void main() {
  vPhase = phase;
  vProbability = probability;
  
  vec3 pos = position;
  
  // Quantum uncertainty visualization
  pos += sin(phase + time) * 0.1 * sqrt(probability);
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  gl_PointSize = 5.0 + probability * 10.0;
}`,
        fragmentShader: `// Quantum Particle Fragment Shader
varying float vPhase;
varying float vProbability;

void main() {
  vec2 center = gl_PointCoord - 0.5;
  float dist = length(center);
  
  if (dist > 0.5) discard;
  
  // Quantum phase visualization
  vec3 color = vec3(
    0.5 + 0.5 * cos(vPhase),
    0.5 + 0.5 * cos(vPhase + 2.094),
    0.5 + 0.5 * cos(vPhase + 4.188)
  );
  
  float alpha = vProbability * (1.0 - dist * 2.0);
  gl_FragColor = vec4(color, alpha);
}`
      },
      documentation: {
        overview: "Advanced quantum mechanics visualization that demonstrates fundamental principles through interactive particle simulations.",
        approach: "Implements simplified Schrödinger equation solver with WebGL compute shaders for real-time quantum state evolution.",
        challenges: [
          "Accurately representing quantum superposition",
          "Balancing scientific accuracy with visual clarity",
          "Managing computational complexity"
        ],
        improvements: [
          "Add quantum entanglement visualization",
          "Implement more quantum gates",
          "Add educational mode with explanations"
        ]
      }
    }
  ];

  const stats = {
    totalExperiments: 12,
    activeProjects: 4,
    communityVotes: 342,
    codeViews: 1847
  };

  // Filter experiments based on current filters
  useEffect(() => {
    let filtered = experiments;

    // Category filter
    if (activeCategory !== 'all') {
      filtered = filtered?.filter(exp => exp?.category === activeCategory);
    }

    // Status filter
    if (activeStatus !== 'all') {
      filtered = filtered?.filter(exp => exp?.status === activeStatus);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(exp =>
        exp?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        exp?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        exp?.technologies?.some(tech => tech?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      );
    }

    // Sort experiments
    switch (sortBy) {
      case 'popular':
        filtered?.sort((a, b) => b?.votes - a?.votes);
        break;
      case 'complexity':
        filtered?.sort((a, b) => b?.difficulty - a?.difficulty);
        break;
      case 'alphabetical':
        filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
        break;
      default: // recent
        filtered?.sort((a, b) => b?.id - a?.id);
    }

    setFilteredExperiments(filtered);
  }, [activeCategory, activeStatus, searchQuery, sortBy]);

  const handleExperimentInteract = (experimentId, paramName, value) => {
    console.log(`Interacting with experiment ${experimentId}:`, paramName, value);
    // Handle parameter changes for real-time interaction
  };

  const handleViewCode = (experimentId) => {
    const experiment = experiments?.find(exp => exp?.id === experimentId);
    setSelectedExperiment(experiment);
    setIsCodeViewerOpen(true);
  };

  const handleVote = (experimentId) => {
    console.log(`Voting for experiment ${experimentId}`);
    // Handle voting logic
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Experimental Playground - 3D Portfolio Showcase</title>
        <meta name="description" content="Explore cutting-edge experiments in 3D web development, AI integration, and emerging technologies. Interactive laboratory for creative innovation." />
      </Helmet>
      <Header />
      <ParticleBackground />
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-accent mb-6">
                <Icon name="Beaker" size={16} />
                <span>EXPERIMENTAL LABORATORY</span>
              </div>
              
              <h1 className="font-headline font-black text-4xl lg:text-6xl text-foreground mb-6">
                Creative
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-cosmic-energy ml-4">
                  Experiments
                </span>
              </h1>
              
              <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Explore the cutting edge of web technology through interactive experiments. 
                From AI-powered visualizations to quantum simulations, discover what's possible 
                when creativity meets innovation.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                  className="bg-accent hover:bg-accent/90 text-background"
                >
                  Start Exploring
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Code"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  View Source Code
                </Button>
              </div>
            </div>

            {/* Stats */}
            <ExperimentStats stats={stats} />
          </div>
        </section>

        {/* Main Content */}
        <section className="pb-24 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar Filters */}
              <div className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <ExperimentFilters
                    activeCategory={activeCategory}
                    onCategoryChange={setActiveCategory}
                    activeStatus={activeStatus}
                    onStatusChange={setActiveStatus}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                  />
                </div>
              </div>

              {/* Experiments Grid */}
              <div className="flex-1">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="font-headline font-bold text-2xl text-foreground">
                      {filteredExperiments?.length} Experiments
                    </h2>
                    <p className="text-muted-foreground text-sm mt-1">
                      {activeCategory !== 'all' && `Filtered by ${activeCategory} • `}
                      {activeStatus !== 'all' && `Status: ${activeStatus} • `}
                      Sorted by {sortBy}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Grid3X3"
                      className="text-muted-foreground hover:text-foreground"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="List"
                      className="text-muted-foreground hover:text-foreground"
                    />
                  </div>
                </div>

                {/* Experiments Grid */}
                {filteredExperiments?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredExperiments?.map((experiment) => (
                      <ExperimentCard
                        key={experiment?.id}
                        experiment={experiment}
                        onInteract={handleExperimentInteract}
                        onViewCode={handleViewCode}
                        onVote={handleVote}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-headline font-bold text-xl text-foreground mb-2">
                      No experiments found
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your filters or search query to find more experiments.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setActiveCategory('all');
                        setActiveStatus('all');
                        setSearchQuery('');
                      }}
                      iconName="RotateCcw"
                      iconPosition="left"
                      className="border-accent/30 text-accent hover:bg-accent/10"
                    >
                      Reset Filters
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Community Section */}
        <section className="py-16 px-6 lg:px-8 border-t border-border/20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-headline font-bold text-3xl text-foreground mb-4">
              Join the Innovation Community
            </h2>
            <p className="font-body text-lg text-muted-foreground mb-8">
              Contribute ideas, vote on future experiments, and collaborate on cutting-edge projects.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-panel rounded-lg p-6 hover:cosmic-glow-hover transition-all duration-300">
                <Icon name="MessageSquare" size={32} className="text-accent mx-auto mb-4" />
                <h3 className="font-headline font-bold text-foreground mb-2">Suggest Ideas</h3>
                <p className="text-muted-foreground text-sm">
                  Share your experimental concepts and see them come to life.
                </p>
              </div>
              
              <div className="glass-panel rounded-lg p-6 hover:cosmic-glow-hover transition-all duration-300">
                <Icon name="Vote" size={32} className="text-cosmic-energy mx-auto mb-4" />
                <h3 className="font-headline font-bold text-foreground mb-2">Vote & Prioritize</h3>
                <p className="text-muted-foreground text-sm">
                  Help decide which experiments get developed next.
                </p>
              </div>
              
              <div className="glass-panel rounded-lg p-6 hover:cosmic-glow-hover transition-all duration-300">
                <Icon name="Users" size={32} className="text-success mx-auto mb-4" />
                <h3 className="font-headline font-bold text-foreground mb-2">Collaborate</h3>
                <p className="text-muted-foreground text-sm">
                  Work together on complex experimental projects.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Code Viewer Modal */}
      <CodeViewer
        experiment={selectedExperiment}
        isOpen={isCodeViewerOpen}
        onClose={() => setIsCodeViewerOpen(false)}
      />
    </div>
  );
};

export default ExperimentalPlayground;